import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  Alert,
  Typography,
} from '@mui/material';
import { JiraCredentials } from '../types';
import TokenInstructions from '../../../components/TokenInstructions';

interface JiraAuthModalProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (credentials: JiraCredentials) => Promise<void>;
  isAuthenticated: boolean;
}

const JiraAuthModal: React.FC<JiraAuthModalProps> = ({
  open,
  onClose,
  onSubmit,
  isAuthenticated,
}) => {
  const [jiraBaseUrl, setJiraBaseUrl] = useState('https://jira.globus-ltd.com');
  const [jiraToken, setJiraToken] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async () => {
    if (!jiraBaseUrl.trim() || !jiraToken.trim()) {
      setError('Все поля обязательны для заполнения');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      await onSubmit({
        jira_base_url: jiraBaseUrl.trim(),
        jira_token: jiraToken.trim(),
      });
      handleClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Произошла ошибка при авторизации');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      setJiraBaseUrl('https://jira.globus-ltd.com');
      setJiraToken('');
      setError(null);
      onClose();
    }
  };

  const isFormValid = jiraBaseUrl.trim().length > 0 && jiraToken.trim().length > 0;

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>{isAuthenticated ? 'Выход из Jira' : 'Авторизация в Jira'}</DialogTitle>
      {isAuthenticated ? (
        <>
          <DialogContent>
            <Box sx={{ mt: 1 }}>
              {error && <Alert severity="error">{error}</Alert>}
              <Typography variant="body1">
                Вы уже авторизованы в Jira. Нажмите «Выйти», чтобы отключить интеграцию.
              </Typography>
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleClose} disabled={loading}>
              Отмена
            </Button>
            <Button
              onClick={async () => {
                setLoading(true);
                setError(null);
                try {
                  await onSubmit({ jira_base_url: '', jira_token: '' });
                  handleClose();
                } catch (err) {
                  setError(
                    err instanceof Error ? err.message : 'Произошла ошибка при выходе из Jira'
                  );
                } finally {
                  setLoading(false);
                }
              }}
              color="error"
              variant="contained"
              disabled={loading}
            >
              {loading ? 'Выход...' : 'Выйти'}
            </Button>
          </DialogActions>
        </>
      ) : (
        <>
          <DialogContent>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
              {error && <Alert severity="error">{error}</Alert>}

              <TextField
                label="Хост Jira"
                placeholder="https://your-company.atlassian.net"
                value={jiraBaseUrl}
                onChange={(e) => setJiraBaseUrl(e.target.value)}
                fullWidth
                required
                disabled={loading}
                helperText="Введите полный URL вашего Jira хоста"
              />

              <TextField
                label="Токен доступа"
                placeholder="atlassian-token-xxxxxxxxxxxxxxxxxxxxxxxx"
                value={jiraToken}
                onChange={(e) => setJiraToken(e.target.value)}
                fullWidth
                required
                disabled={loading}
                type="password"
                helperText="Введите ваш персональный токен доступа Jira"
              />

              <TokenInstructions
                steps={[
                  'Войдите в Jira.',
                  'Откройте меню профиля и перейдите в раздел «Личные маркеры доступа» (Personal Access Tokens).',
                  'Нажмите «Создать маркер» (Create token) и задайте название, например «Globus Context».',
                  'Скопируйте сгенерированный маркер — он показывается только один раз.',
                  'Вставьте маркер в поле «Токен доступа», а в поле «Хост Jira» укажите адрес вашего сайта.',
                ]}
              />
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleClose} disabled={loading}>
              Отмена
            </Button>
            <Button onClick={handleSubmit} variant="contained" disabled={!isFormValid || loading}>
              {loading ? 'Сохранение...' : 'Сохранить'}
            </Button>
          </DialogActions>
        </>
      )}
    </Dialog>
  );
};

export default JiraAuthModal;
